// Окно после входа в приложение: сначала — снижение рейтинга за «чушь» (если было), потом —
// награда за ежедневный вход (баллы, серия, бонус за неделю, новые достижения, потери за дни без входа).
// items — очередь: { kind: 'penalty', news: [...] } | { kind: 'reward', reward, rating }
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { RatingApi } from '../../components_android/api/RatingApi';
import { Pages } from '../custom_views/insta_custom_views/utils_android/UtilsAndroid';
import { BAD_ANSWER_ADVICE, CUSTOMER_DOWN_ADVICE, daysText, levelColor, signed } from './ratingTexts';
import '../../css_android/rating.css';

function RatingLine({ rating }) {
    if (!rating) return null;
    const color = levelColor(rating.level);
    return (
        <div className="rating_popup_now">
            Рейтинг: <strong style={{ color: color }}>{rating.value}</strong> из {(rating.rules && rating.rules.max) || 100}
            <span className="rating_level" style={{ color: color, background: color + '1A' }}>{rating.level_title}</span>
        </div>
    );
}

// Снижение рейтинга: «чушь в ответе» (bad_answer) и понижение от заказчика (customer)
function Penalty({ item, rating }) {
    const news = item.news;
    const total = news.reduce((s, n) => s + (n.delta || 0), 0);
    const names = news.map((n) => n.order_name).filter(Boolean);
    const onlyBad = news.every((n) => n.reason !== 'customer');
    const onlyCustomer = news.every((n) => n.reason === 'customer');
    const one = news[0];
    let text;
    if (news.length === 1 && one.reason === 'customer') {
        text = <>Заказчик{one.order_name ? <>, с которым вы переписывались по заданию <strong>«{one.order_name}»</strong>,</> : ''} понизил ваш рейтинг.</>;
    } else if (news.length === 1) {
        text = <>Заказчик отметил ваш ответ{one.order_name ? <> в задании <strong>«{one.order_name}»</strong></> : ''} как «чушь».</>;
    } else if (onlyBad) {
        text = <>Заказчики отметили ваши ответы как «чушь» в {news.length} заданиях{names.length ? ':' : '.'}</>;
    } else if (onlyCustomer) {
        text = <>Заказчики понизили ваш рейтинг ({news.length}){names.length ? ':' : '.'}</>;
    } else {
        text = <>Рейтинг снизили заказчики:</>;
    }
    const lines = news.slice(0, 5).map((n) => {
        if (onlyBad || onlyCustomer) return n.order_name ? '«' + n.order_name + '»' : '';
        return (n.order_name ? '«' + n.order_name + '» — ' : '') + (n.reason === 'customer' ? 'заказчик понизил рейтинг' : 'ответ отмечен как «чушь»');
    }).filter(Boolean);
    return (
        <>
            <div className="rating_popup_badge is-down">{signed(total)}</div>
            <div className="rating_popup_title">Рейтинг снижен</div>
            <p className="rating_popup_text">{text}</p>
            {news.length > 1 && lines.length > 0 && (
                <ul className="rating_popup_list">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
            )}
            <p className="rating_popup_text rating_popup_advice">{onlyBad ? BAD_ANSWER_ADVICE : CUSTOMER_DOWN_ADVICE}</p>
            <RatingLine rating={rating} />
        </>
    );
}

function Reward({ item }) {
    const r = item.reward;
    const total = (r.daily || 0) + (r.week || 0);
    return (
        <>
            <div className="rating_popup_badge is-up">{signed(total)}</div>
            <div className="rating_popup_title">{r.week > 0 ? daysText(r.streak) + ' подряд!' : 'Ежедневный вход'}</div>
            <p className="rating_popup_text">
                {total > 0 ? <>{signed(total)} к рейтингу за вход{r.week > 0 ? <>, из них {signed(r.week)} — за неделю без пропусков</> : ''}.</> : 'Рейтинг уже максимальный — так держать!'}
            </p>
            <div className="rating_popup_streak">{r.streak > 1 ? '🔥 ' + daysText(r.streak) + ' подряд' : '🔥 Серия началась — заходите завтра'}</div>
            {r.decay > 0 && (
                <p className="rating_popup_text rating_popup_decay">Вы не заходили {daysText(r.missed_days)}: рейтинг снизился на {r.decay}.</p>
            )}
            {(r.achievements || []).length > 0 && (
                <div className="rating_popup_achievements">
                    {r.achievements.map((a) => (
                        <div key={a.id} className="rating_popup_achievement">
                            <span className="rating_popup_ach_icon" aria-hidden="true">{a.icon}</span>
                            <span>
                                <span className="rating_popup_ach_label">Новое достижение</span>
                                <span className="rating_popup_ach_title">{a.title}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <RatingLine rating={item.rating} />
        </>
    );
}

export default function RatingPopup({ items, rating, onDone }) {
    const navigate = useNavigate();
    const [leaving, setLeaving] = useState(false);
    const item = items && items[0];
    useEffect(() => { setLeaving(false); }, [item]);
    if (!item) return null;

    const close = (more) => {
        if (leaving) return;
        if (item.kind === 'penalty') RatingApi.seen(item.news.map((n) => n.id));
        setLeaving(true);
        setTimeout(() => {
            onDone();
            if (more) navigate('/' + Pages.rating);
        }, 220);
    };
    const isPenalty = item.kind === 'penalty';
    return createPortal(
        <div className={'rating_popup_overlay' + (leaving ? ' is-leaving' : '')} role="dialog" aria-modal="true"
            aria-label={isPenalty ? 'Рейтинг снижен' : 'Награда за вход'} onClick={(e) => { if (e.target === e.currentTarget) close(false); }}>
            <div className={'rating_popup' + (isPenalty ? ' is-penalty' : ' is-reward')}>
                {isPenalty ? <Penalty item={item} rating={rating} /> : <Reward item={item} />}
                <div className="rating_popup_actions">
                    <div className="task_btn rating_popup_ok hover" onClick={() => close(false)}>{isPenalty ? 'Понятно' : 'Отлично'}</div>
                    <div className="rating_popup_more hover" onClick={() => close(true)}>Подробнее о рейтинге</div>
                </div>
            </div>
        </div>,
        document.body
    );
}
